<script setup lang="ts">
import type { Component } from 'vue'

const props = withDefaults(defineProps<{
  size?: 'md' | 'sm'
  /** In page content rather than the header: see `WalletAuthControl`. */
  inline?: boolean
}>(), {
  size: 'md',
  inline: false,
})

// The wallet stack is a large share of the app's script, and most visitors to the landing
// page never touch the button. So this renders a plain button that looks the same, and loads
// the real one (`WalletAuthControl`) only when it is needed: once there is a session to show,
// or when the visitor reaches for the button. A click made before it has loaded connects as
// soon as it has.
const { $loadWagmi } = useNuxtApp()
const user = useSupabaseUser()

const control = shallowRef<Component | null>(null)
const connectOnMount = ref(false)
const isLoading = ref(false)
let loading: Promise<void> | null = null

function load() {
  loading ??= Promise.all([$loadWagmi(), import('./WalletAuthControl.vue')])
    .then(([, module]) => {
      control.value = markRaw(module.default)
    })
    .catch((error: unknown) => {
      loading = null
      isLoading.value = false
      console.error('[wallet] Failed to load the wallet button:', error)
    })
  return loading
}

function connect() {
  connectOnMount.value = true
  isLoading.value = true
  void load()
}

onMounted(() => {
  watch(() => user.value?.sub, (sub) => {
    if (sub != null && sub !== '')
      scheduleIdleCallback(() => void load(), { timeout: 2000 })
  }, { immediate: true })
})

const placeholderLabel = computed(() =>
  props.size === 'sm' ? 'Sign in' : 'Sign in with Coinbase',
)
</script>

<template>
  <component
    :is="control"
    v-if="control"
    :size="props.size"
    :inline="props.inline"
    :connect-on-mount="connectOnMount"
  />
  <div
    v-else
    class="relative flex items-center"
    :class="[props.size === 'sm' ? 'h-9' : 'h-10', props.inline ? 'justify-center' : 'justify-end']"
  >
    <UIButton
      variant="base"
      :size="props.size"
      :loading="isLoading"
      @pointerenter="load"
      @focus="load"
      @click="connect"
    >
      <UIBaseLogo v-if="!isLoading" :size="props.size" />
      <span class="whitespace-nowrap">{{ placeholderLabel }}</span>
    </UIButton>
  </div>
</template>
